<script setup lang="ts">
import RadioButton from '@/components/Buttons/RadioButton.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import deviceConfigApi from '@/services/deviceConfig.service'
import { EColors } from '@/types/constants/ColorValues'
import {
    EDeviceStatus,
    EDeviceStatusColors,
    EDeviceStatusLabels
} from '@/types/enum/DeviceStatusEnum'
import type { DeviceFirmwaresModel } from '@/types/models/DeviceFirmwaresModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import moment from 'moment'
import { computed, ref, watch } from 'vue'
import type { PaginationDataType } from '../../../../../../types/PaginationType'

type Props = {
    device: Partial<DeviceModel>
    versionId?: number
}
const props = defineProps<Props>()
type Emits = {
    (e: 'change', version_id: number): void
}
const emit = defineEmits<Emits>()

const deviceStatus = computed(() =>
    props.device.status
        ? (parseInt(props.device.status as string) as EDeviceStatus)
        : EDeviceStatus.OFFLINE
)

const isRented = computed(
    () =>
        props.device.rental?.start_at &&
        props.device.rental?.end_at &&
        moment().isAfter(moment(props.device.rental.start_at)) &&
        moment().isBefore(moment(props.device.rental.end_at))
)

const versions = ref<DeviceFirmwaresModel[]>([])

const getAvailableVersions = () =>
    deviceConfigApi
        .getDeviceFirmwares({
            filters: {
                device_type_id: props.device.device_type_id,
                device_id: props.device.device_id ?? props.device.id
            },
            per_page: -1
        })
        .then(
            res =>
                (versions.value =
                    (res as PaginationDataType<DeviceFirmwaresModel[]>)?.data ??
                    (res as DeviceFirmwaresModel[]) ??
                    [])
        )
watch(
    () => props.device.device_type_id,
    () => getAvailableVersions(),
    { immediate: true }
)
</script>

<template>
    <StackLayout direction="column" :gap="8">
        <TypographyItem bold :label="$t('device.updateVersion.stepTwo.title')" />
        <ul class="step-two-list">
            <li>
                <StackLayout class="history-line" :gap="4">
                    <TypographyItem :label="device.serial_number" />
                    <TypographyItem label="-" />
                    <TypographyItem :label="device.name" />
                    <TypographyItem label="-" />
                    <TypographyItem
                        :label="$t(EDeviceStatusLabels[deviceStatus])"
                        :class="`has-text-${EDeviceStatusColors[deviceStatus]}`" />
                    <TypographyItem label="-" />
                    <TypographyItem
                        :label="
                            $t('device.updateVersion.stepTwo.battery', { battery: device.battery })
                        "
                        :class="
                            parseInt((device.battery ?? 0).toString()) < 50
                                ? `has-text-${EColors.DANGER}`
                                : ''
                        " />
                    <TypographyItem label="-" />
                    <TypographyItem
                        :label="
                            $t('device.updateVersion.stepTwo.version', { version: device.version })
                        " />
                    <TypographyItem label="-" />
                    <TypographyItem
                        :label="$t('device.updateVersion.stepTwo.notAffected')"
                        :class="device.event ? `has-text-${EColors.PRIMARY}` : ''" />
                    <TypographyItem label="-" />
                    <TypographyItem
                        :label="$t('device.updateVersion.stepTwo.notRented')"
                        :class="isRented ? `has-text-${EColors.DANGER}` : ''" />
                </StackLayout>
            </li>
        </ul>

        <TypographyItem bold :label="$t('device.updateVersion.stepTwo.versionSelection')" />
        <RadioButton
            v-if="versions.length"
            direction="column"
            valueKey="id"
            :modelValue="versionId"
            :options="versions"
            @change="$emit('change', $event)" />
        <TypographyItem v-else :label="$t('common.noOption')" />
    </StackLayout>
</template>

<style scoped lang="scss">
.step-two-list {
    margin-left: 2em;
    list-style-type: disc;
}

.history-line {
    white-space: pre;
    flex-flow: wrap;
}
</style>
